import React from 'react'
import { useQuery } from 'react-query'
import { Link } from 'react-router-dom'
import {
  BriefcaseIcon,
  BuildingStorefrontIcon,
  ChartBarIcon,
  ClockIcon,
  CurrencyDollarIcon,
  ArrowTrendingUpIcon,
} from '@heroicons/react/24/outline'
import { api } from '../services/api.ts'
import LoadingSpinner from '../components/LoadingSpinner.tsx'
import StatsCard from '../components/StatsCard.tsx'
import RecentJobs from '../components/RecentJobs.tsx'
import QuickActions from '../components/QuickActions.tsx'

export default function Dashboard() {
  const { data: stats, isLoading: statsLoading } = useQuery(
    'dashboard-stats',
    () => api.get('/api/jobs/stats/overview'),
    {
      refetchInterval: 30000, // Refresh every 30 seconds
    }
  )

  const { data: recentJobs, isLoading: jobsLoading } = useQuery(
    'recent-jobs',
    () => api.get('/api/jobs/?limit=5'),
    {
      refetchInterval: 30000,
    }
  )

  const { data: costAnalytics, isLoading: costLoading } = useQuery(
    'cost-analytics',
    () => api.get('/api/jobs/analytics/costs'),
    {
      refetchInterval: 60000, // Refresh every minute
    }
  )

  if (statsLoading || jobsLoading || costLoading) {
    return <LoadingSpinner />
  }

  return (
    <div className='space-y-6'>
      {/* Header */}
      <div>
        <h1 className='text-2xl font-bold text-gray-900 dark:text-white'>
          Dashboard
        </h1>
        <p className='mt-1 text-sm text-gray-500 dark:text-gray-400'>
          Overview of your VaporScraper jobs and stores
        </p>
      </div>

      {/* Stats Cards */}
      <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
        <StatsCard
          title='Total Jobs'
          value={stats?.data?.total_jobs || 0}
          icon={BriefcaseIcon}
          color='blue'
        />
        <StatsCard
          title='Total Stores'
          value={stats?.data?.total_stores || 0}
          icon={BuildingStorefrontIcon}
          color='green'
        />
        <StatsCard
          title='States Covered'
          value={Object.keys(stats?.data?.stores_by_state || {}).length}
          icon={ChartBarIcon}
          color='purple'
        />
        <StatsCard
          title='Active Jobs'
          value={
            recentJobs?.data?.filter((job: any) => job.status === 'running')
              .length || 0
          }
          icon={ClockIcon}
          color='yellow'
        />
      </div>

      {/* Cost Analytics Cards */}
      <div className='grid grid-cols-1 gap-5 sm:grid-cols-2 lg:grid-cols-4'>
        <StatsCard
          title='Total API Costs'
          value={`$${costAnalytics?.data?.total_costs?.toFixed(2) || '0.00'}`}
          icon={CurrencyDollarIcon}
          color='green'
        />
        <StatsCard
          title='Average Cost per Job'
          value={`$${
            costAnalytics?.data?.avg_cost_per_job?.toFixed(2) || '0.00'
          }`}
          icon={ArrowTrendingUpIcon}
          color='blue'
        />
        <StatsCard
          title='Cost This Month'
          value={`$${costAnalytics?.data?.monthly_costs?.toFixed(2) || '0.00'}`}
          icon={ChartBarIcon}
          color='purple'
        />
        <StatsCard
          title='Most Expensive State'
          value={costAnalytics?.data?.most_expensive_state || 'N/A'}
          icon={BriefcaseIcon}
          color='yellow'
        />
      </div>

      {/* Quick Actions */}
      <QuickActions />

      {/* Recent Jobs */}
      <RecentJobs jobs={recentJobs?.data || []} />

      {/* Charts */}
      <div className='grid grid-cols-1 gap-5 lg:grid-cols-2'>
        {/* Stores by State */}
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg leading-6 font-medium text-gray-900 dark:text-gray-400'>
              Stores by State
            </h3>
          </div>
          <div className='card-body'>
            <div className='space-y-3'>
              {Object.entries(stats?.data?.stores_by_state || {})
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 10)
                .map(([state, count]) => (
                  <div
                    key={state}
                    className='flex items-center justify-between'
                  >
                    <span className='text-sm font-medium text-gray-900 dark:text-gray-400'>
                      {state}
                    </span>
                    <span className='text-sm text-gray-500 dark:text-gray-300'>
                      {count as number}
                    </span>
                  </div>
                ))}
            </div>
            <div className='mt-4'>
              <Link
                to='/stores'
                className='text-sm text-blue-600 hover:text-blue-500'
              >
                View all stores →
              </Link>
            </div>
          </div>
        </div>

        {/* Stores by Keyword */}
        <div className='card'>
          <div className='card-header'>
            <h3 className='text-lg leading-6 font-medium text-gray-900 dark:text-gray-400'>
              Stores by Keyword
            </h3>
          </div>
          <div className='card-body'>
            <div className='space-y-3'>
              {Object.entries(stats?.data?.stores_by_keyword || {})
                .sort(([, a], [, b]) => (b as number) - (a as number))
                .slice(0, 10)
                .map(([keyword, count]) => (
                  <div
                    key={keyword}
                    className='flex items-center justify-between'
                  >
                    <span className='text-sm font-medium text-gray-900 dark:text-gray-400'>
                      {keyword}
                    </span>
                    <span className='text-sm text-gray-500 dark:text-gray-300'>
                      {count as number}
                    </span>
                  </div>
                ))}
            </div>
            <div className='mt-4'>
              <Link
                to='/settings'
                className='text-sm text-blue-600 hover:text-blue-500'
              >
                Manage keywords →
              </Link>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}
